import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Stack,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,Tooltip
} from '@mui/material';

import { DataGrid } from '@mui/x-data-grid';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteIcon from '@mui/icons-material/Delete';

import TitleWithAddButton from '../Home/TitleWithAddButton';
import ReusableDrawer from '../../Components/ReusableDrawer';
import StyledInputField from '../../Components/Custom/StyledInputField';
import MuiTypographyMandatory from '../../Components/Custom/MuiTypographyMandatory';
import MUITypography from '../../Components/MUITypography';
import Loader from '../../Components/Loader';

import { align_center, align_column,  dataGridStyle, VM_Colors } from '../../Styles/VMStyles';
import { toast } from 'react-toastify';
import StorageFactory from '../../Util/StorageFactory';
import { SESSION_USER_ID_KEY } from '../../Util/VMConstants';
import {
  API_ADD_UPDATE_CHECKLIST,
  API_DELETE_CHECKLIST_DATA,
  API_GET_CHECKLIST_DATA,
} from '../../Util/URLConstants';
import axios from 'axios';
import { formattedDateTime, renderSNo } from '../../Util/UtilHelper';
import { canMutateRecord } from '../../Util/recordAuthorization';
import { roleOptions } from '../../Util/ConstnatUtils';



function CheckList() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [checklistId, setChecklistId] = useState(null);
  const [checklistData, setChecklistData] = useState({
    checklist_name: '',
    role: '',
    checklist_point: '',
  });
  const [checklistPoints, setChecklistPoints] = useState([]);
  const [errors, setErrors] = useState({
    checklist_name: '',
    role: '',
    checklist_point: '',
  });

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_GET_CHECKLIST_DATA}?user_id=${userID}`;
      const res = await fetch(url);
      const resp = await res.json();

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        const formattedData = resp.data.map((cls, idx) => ({
          id: cls.id || `${idx}`,
          name: cls.checklist_name || '',
          role: cls.role,
          checklist_points: cls.checklist_points,
          created_date: formattedDateTime(cls.created_date),
          created_by: cls.created_by,
        }));
        setRows(formattedData);
      } else {
        toast.error(resp.message || 'Unexpected response from server');
        setRows([]);
      }
    } catch (err) {
      console.error('Error fetching checklists:', err);
      toast.error('Failed to load checklists');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userID]);

  const handleEditChecklist = (rowData) => {
    setChecklistData({
      checklist_name: rowData.name,
      role: rowData.role,
      checklist_point: '',
    });
    setChecklistPoints(rowData.checklist_points || []);
    setChecklistId(rowData.id);
    setErrors({
      checklist_name: '',
      role: '',
      checklist_point: '',
    });
    setDrawerOpen(true);
  };

  const handleDeleteChecklist = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this checklist?");
    if (!confirm) return;

    try {
      const response = await axios.get(API_DELETE_CHECKLIST_DATA, {
        params: {
          user_id: userID,
          id,
        },
      });

      const data = response.data;

      if (data.status_code === 200) {
        toast.success("Checklist removed successfully");
        fetchData();
      } else {
        toast.error(data.message || 'Failed to delete checklist');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error deleting checklist');
    }
  };

  const columns = [
        renderSNo(),

    { field: 'name', headerName: 'Checklist Name', flex: 0.5 },
    { field: 'role', headerName: 'Role', flex: 0.5 },
    {
  field: 'checklist_points',
  headerName: 'Checklist Points',
  flex: 1,
  sortable: false,
  renderCell: (params) => {
    const points = Array.isArray(params.row.checklist_points)
      ? params.row.checklist_points
      : typeof params.row.checklist_points === 'string'
        ? params.row.checklist_points.split(',').map(p => p.trim())
        : [];

    return (
      <Tooltip
        title={
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {points.map((point, index) => (
              <li key={index}>
                <Typography variant="body2" p={0.5}>{point}</Typography>
              </li>
            ))}
          </Box>
        }
        placement="top-start"
        arrow
      >
        <Typography noWrap pt={1}>
          {points.slice(0, 2).join(', ')}
          {points.length > 2 ? `,  +${points.length - 2}  more` : ''}
        </Typography>
      </Tooltip>
    );
  },
},


    { field: 'created_date', headerName: 'Created Date', flex: 0.5 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.3,
      renderCell: (params) => (
        <Box sx={align_center}>
          {canMutateRecord(null, null, params.row) && (
            <IconButton onClick={() => handleEditChecklist(params.row)} color="primary">
              <EditNoteIcon />
            </IconButton>
          )}
          {canMutateRecord(null, null, params.row) && (
            <IconButton onClick={() => handleDeleteChecklist(params.row.id)} color="error">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCheckListHandler = () => {
    setChecklistData({
      checklist_name: '',
      role: '',
      checklist_point: '',
    });
    setChecklistPoints([]);
    setChecklistId(null);
    setErrors({
      checklist_name: '',
      role: '',
      checklist_point: '',
    });
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleChange = (field) => (event) => {
    setChecklistData((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateFields = (actionType) => {
    const newErrors = {
      checklist_name: checklistData.checklist_name.trim() === '' ? 'Checklist Name is required' : '',
      role: checklistData.role === '' ? 'Please select a role' : '',
      checklist_point:
        actionType === 'add' && checklistData.checklist_point.trim() === ''
          ? 'Checklist Point is required'
          : '',
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === '');
  };

  const handleAddPoint = () => {
    if (!validateFields('add')) return;
    setChecklistPoints((prev) => [...prev, checklistData.checklist_point.trim()]);
    setChecklistData((prev) => ({ ...prev, checklist_point: '' }));
  };

  const submitHandler = async () => {
    const isValid = validateFields('submit');

    if (!isValid || checklistPoints.length === 0) {
      if (checklistPoints.length === 0) {
        setErrors((prev) => ({
          ...prev,
          checklist_point: 'Add at least one checklist point',
        }));
        toast.error('Add at least one checklist point');
      }
      return;
    }

    const finalPayload = {
      checklist_name: checklistData.checklist_name.trim(),
      role: checklistData.role,
      checklist_points: checklistPoints,
      user_id: userID,
      ...(checklistId && { id: checklistId }),
    };

    try {
      setLoading(true);
      const response = await axios.post(API_ADD_UPDATE_CHECKLIST, finalPayload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const respData = response.data;
      if (respData.status_code === 200) {
        toast.success('Checklist saved successfully');
        handleDrawerClose();
        fetchData();
      } else {
        toast.error(respData.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error.message);
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <TitleWithAddButton
        title="Check List"
        onAddClick={addCheckListHandler}
        buttonLabel="Create CheckList"
      />

      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        autoHeight
        loading={loading}
        rowsPerPageOptions={[5, 10, 20, 50]}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        getRowHeight={() => 'auto'} 
        hideFooterSelectedRowCount
        sx={dataGridStyle}
      />

      <ReusableDrawer
        open={drawerOpen}
        title="Create Checklist"
        onClose={handleDrawerClose}
        onSubmit={submitHandler}
        showFooter
        loading={loading}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <StyledInputField
              heading="Checklist Name"
              value={checklistData.checklist_name}
              onChange={handleChange('checklist_name')}
              fullWidth
              required
              error={Boolean(errors.checklist_name)}
              helperText={errors.checklist_name}
            />

            <Box sx={align_column}>
              <MuiTypographyMandatory label="Role" required />
              <Select
                name="role"
                value={checklistData.role}
                onChange={handleChange('role')}
                error={Boolean(errors.role)}
                sx={{ backgroundColor: VM_Colors.white }}
                displayEmpty
              >
                <MenuItem value="" disabled>  -- Select --   </MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
              {errors.role && (
                <MUITypography fontSize={12} color="error">
                  {errors.role}
                </MUITypography>
              )}
            </Box>

            <StyledInputField
              heading="Enter Checklist Point"
              value={checklistData.checklist_point}
              onChange={handleChange('checklist_point')}
              fullWidth
              multiline
              rows={3}
              error={Boolean(errors.checklist_point)}
              helperText={errors.checklist_point}
              required= {true}
            />

            <Button variant="contained" onClick={handleAddPoint}>
              Add Checklist Point
            </Button>

            {checklistPoints.length > 0 && (
              <Box>
                <Typography fontWeight={600} sx={{ mt: 2 }}>
                  Checklist Points Added:
                </Typography>
                <Card variant="outlined" sx={{ mt: 1, borderRadius: 2 }}>
                  <CardContent>
                    <ul>
                      {checklistPoints.map((point, idx) => (
                        <li key={idx}>
                          <Typography fontSize={14}>{point}</Typography>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Stack>
        </Box>
      </ReusableDrawer>
      <Loader loading={loading} />
    </Box>
  );
}

export default CheckList;
